/**
 * @file hostHooks.ts
 * @description 固件升级宿主钩子：由应用层注入 UI（如 rapoo-element 弹窗），
 * SDK 不依赖具体组件库。WebHID 授权必须在用户点击弹窗主按钮后的手势链上继续。
 *
 * 注意：传给宿主的 boot 身份只含展示文案，不含 VID/PID，避免 Web 层与具体 Boot 设备强耦合。
 * Boot 的 USB 身份仅由各设备 manifest 的 update filter 提供，在芯片实现内解析。
 */

/** 确认进入 Boot（可取消）；授权 Boot 设备（不可关闭） */
export type FirmwareAuthPromptKind = 'confirmEnterBoot' | 'authorizeBootDevice'

/** 宿主弹窗用的 Boot 提示（无 VID/PID） */
export interface FirmwareAuthBootIdentity {
  /**
   * 用户可见名称提示（不含 VID/PID）。
   * 应为系统选择器中可能出现的真实产品名示意，如「Volt* Upgrader」。
   */
  label: string
}

export interface FirmwareAuthPromptRequest {
  kind: FirmwareAuthPromptKind
  boot: FirmwareAuthBootIdentity
  /**
   * confirmEnterBoot：true（取消则仍停留 APP）
   * authorizeBootDevice：false（不可关闭；取消授权只能拔插）
   */
  allowCancel: boolean
}

export interface FirmwareHostHooks {
  /**
   * 弹出授权/确认 UI。
   * - resolve：用户点击主按钮（此时仍持有用户手势，可紧接着 requestDevice）
   * - reject：用户取消（仅 allowCancel=true 时）
   */
  requestFirmwareAuth(request: FirmwareAuthPromptRequest): Promise<void>
}

let firmwareHostHooks: FirmwareHostHooks | null = null

/** 应用启动时注册；传 null 清除（测试用） */
export function setFirmwareHostHooks(hooks: FirmwareHostHooks | null): void {
  firmwareHostHooks = hooks
}

export function getFirmwareHostHooks(): FirmwareHostHooks | null {
  return firmwareHostHooks
}

/** 用户在可取消弹窗上点了取消 */
export const FIRMWARE_AUTH_CANCELLED = 'FIRMWARE_AUTH_CANCELLED'

export function isFirmwareAuthCancelled(error: unknown): boolean {
  if (!error) return false
  if (error instanceof Error) {
    return error.name === FIRMWARE_AUTH_CANCELLED || error.message.includes(FIRMWARE_AUTH_CANCELLED)
  }
  return String(error).includes(FIRMWARE_AUTH_CANCELLED)
}
